import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FreelancerProfile, Profile, UserStatus } from '../types/profile';
import { useAdminStore } from '../stores/adminStore';
import FullBlockingModal from './common/FullBlockingModal';
import { Button } from './common/Button';

interface UpdateUserStatusModalProps {
  user: FreelancerProfile | Profile;
  onClose: () => void;
  closeButtonLabel?: string;
}

const UpdateProfileStatusModal: React.FC<UpdateUserStatusModalProps> = ({
  user,
  onClose,
  closeButtonLabel,
}) => {
  const { t } = useTranslation('common');

  const [selectedUserStatus, setSelectedUserStatus] = useState<UserStatus>(
    (user.status as UserStatus) || 'active'
  );

  const userStatuses: UserStatus[] = ['active', 'inactive', 'banned'];

  const username: string = `${user.first_name} ${user?.last_name ?? ''}`;

  const { updateProfileStatus } = useAdminStore();

  const handleConfirm = async () => {
    if (selectedUserStatus) {
      try {
        await updateProfileStatus(user.id, selectedUserStatus);
      } catch {
        // The store already logged the error; keep the modal open so the admin can retry.
        toast.error(t('generic_error', { ns: 'common' }));
        return;
      }
    }
    onClose();
  };

  return (
    <FullBlockingModal innerClassName="md:max-w-[48rem]">
      <h2 className="text-2xl font-bold text-secondary mb-4">
        <Trans
          i18nKey={t('update_user_status', { user: username })}
          values={{ username }}
          components={{ bold: <span style={{ fontWeight: 'bold', color: '#007475' }} /> }}
        />
      </h2>

      <div className="flex flex-col gap-3">
        <label htmlFor="user-status" className="text-sm font-semibold text-gray-700">
          {t('select_status')}
        </label>
        <select
          id="user-status"
          value={selectedUserStatus}
          onChange={e => setSelectedUserStatus(e.target.value as UserStatus)}
          className="border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
        >
          {userStatuses.map(status => (
            <option key={status} value={status}>
              {t(`user_status.${status}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-row md:flex-row justify-end gap-4 mt-8">
        <Button
          variant="simple-text"
          onClick={onClose}
          className="font-bold rounded-full px-8 py-2 w-full md:w-auto"
          type="button"
        >
          {closeButtonLabel || t('common:form_actions.cancel')}
        </Button>

        <Button
          variant="primary"
          onClick={handleConfirm}
          className="font-bold rounded-full px-8 py-2 w-full md:w-auto"
          type="button"
        >
          {t('common:form_actions.confirm', { ns: 'common' })}
        </Button>
      </div>
    </FullBlockingModal>
  );
};

export default UpdateProfileStatusModal;
